import React, { useMemo } from "react";
import createAnalysisChart from "./CreateAnalysisChart";
import { normalizeForPreview, normalizeForExpanded } from "../../../lib/canton-utils";
import { CANTON_COLOR_MAP } from "../../../lib/constants";
import { AnalysisChartProps } from "./types";

const BaseCantonChart = createAnalysisChart({
  chartType: "canton",
  title: "Canton Analysis",
  legendTitle: "Cantons",
  categoryField: "Canton",
  colorMap: CANTON_COLOR_MAP,
  normalizeCategory: normalizeForPreview,
  normalizeCategoryExpanded: normalizeForExpanded,
  volumeLabel: "Invested capital",
  countLabel: "Number of deals",
}) as React.ComponentType<AnalysisChartProps>;

const CantonAnalysisChart: React.FC<AnalysisChartProps> = ({
  deals,
  allDeals,
  selectedCategories,
  ...props
}) => {
  const expandedCategories = useMemo(() => {
    if (!deals?.length) return [] as string[];
    const cantons = [
      ...new Set(deals.map((d) => normalizeForExpanded(d.Canton)).filter(Boolean)),
    ] as string[];
    return cantons.sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
  }, [deals]);

  const normalizedSelectedCategories = useMemo(() => {
    if (!selectedCategories || selectedCategories.length === 0) return [];

    const normalized = new Set<string>();
    selectedCategories.forEach((canton) => {
      const norm = normalizeForPreview(canton);
      if (norm) normalized.add(norm);
    });

    return Array.from(normalized);
  }, [selectedCategories]);

  return (
    <BaseCantonChart
      deals={deals}
      allDeals={allDeals}
      expandedCategories={expandedCategories}
      selectedCategories={normalizedSelectedCategories}
      {...props}
    />
  );
};

export default CantonAnalysisChart;
